import {FC, useEffect, useRef} from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

interface AutoResizePluginProps{
    minHeight?: number;
}

const AutoResizePlugin: FC<AutoResizePluginProps> = ({minHeight = 100}) => {
    const [editor] = useLexicalComposerContext();
    const observerRef = useRef<ResizeObserver | null>(null);
    const lastHeightRef = useRef<number>(0);
    const isAdjustingRef = useRef<boolean>(false);

    useEffect(() => {
        const rootElement = editor.getRootElement();
        if (!rootElement) return;

        const adjustHeight = () => {
            if (isAdjustingRef.current) return;

            isAdjustingRef.current = true;

            requestAnimationFrame(() => {
                rootElement.style.height = "auto";
                const scrollHeight = rootElement.scrollHeight;
                const newHeight = Math.max(scrollHeight, minHeight);

                if (Math.abs(newHeight - lastHeightRef.current) > 1) {
                    rootElement.style.height = `${newHeight}px`;
                    lastHeightRef.current = newHeight;
                }

                isAdjustingRef.current = false;
            });
        };

        const unregister = editor.registerUpdateListener(() => {
            adjustHeight();
        });

        observerRef.current = new ResizeObserver((entries) => {
            if (!isAdjustingRef.current) {
                adjustHeight();
            }
        });

        observerRef.current.observe(rootElement);

        adjustHeight();

        return () => {
            unregister();
            observerRef.current?.disconnect();
        };
    }, [editor, minHeight]);

    return null;
};

export default AutoResizePlugin;
